import styles from "./spendingFilter.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "../../../customHooks/debounce";
import { setFilter } from "../../../features/filter/spendingFilterSlice";
import { CustomDropdown } from "../../common/customDropdown/CustomDropdown ";
import { CustomDatePicker } from "../../common/customDatePicker/CustomDatePicker";
import { useGetAllUserQuery } from "../../../features/api/user/userApi";
import { useGetAllSpendingCategoryQuery } from "../../../features/api/expense/spendingCategoryApi";

export const SpendingFilter = () => {
  const dispatch = useDispatch();
  const filterValueByAmount = useSelector(
    (state) => state.spendingFilter.amount
  );
  const filterValueByCreatedBy = useSelector(
    (state) => state.spendingFilter.createdBy
  );
  const expenseDateStart = useSelector(
    (state) => state.spendingFilter.expenseDateStart
  );

  const expenseDateEnd = useSelector(
    (state) => state.spendingFilter.expenseDateEnd
  );

  const [valueToDelaySearch, setValueToDelaySearch] = useState({
    property: "amount",
    value: filterValueByAmount,
  });
  const [valueToDelaySearchCreatedBy, setValueToDelaySearchCreatedBy] =
    useState({
      property: "createdBy",
      value: filterValueByCreatedBy,
    });
  const debounceValueAmount = useDebounce(valueToDelaySearch);
  const debounceValueCreatedBy = useDebounce(valueToDelaySearchCreatedBy);

  // Filter -
  const {
    data: createdByData,
    isLoading: isCreatedByListLoading,
    isError: createdByDataError,
  } = useGetAllUserQuery();

  // Build Order By Options dynamically
  const createdByOptions = useMemo(() => {
    if (!createdByData || !Array.isArray(createdByData)) return [];

    // If logged-in user is sales executive: show ONLY his own name
    let filteredUsers = createdByData;

    filteredUsers = createdByData.filter((u) =>
      u.roles !== "SALES_EXECUTIVE" ? `${u?.firstName} ${u?.lastName}` : null
    );

    const options = filteredUsers.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: `${user.firstName} ${user.lastName}`,
    }));

    return [{ label: "All", value: "" }, ...options];
  }, [createdByData]);

  const paymentMethodList = ["all", "cash", "check", "bank_transfer"];
  const paymentMethodOptions = paymentMethodList.map((type) => ({
    label: type === "all" ? "All" : type,
    value: type === "all" ? "" : type,
  }));

  // const categoryList = ["all", "car", "maintenance"];
  // const categoryOptions = categoryList.map((type) => ({
  //   label: type === "all" ? "All" : type,
  //   value: type === "all" ? "" : type,
  // }));

  const {
    data: categoryData,
    isLoading: isCategoryDataLoading,
    isError: categoryDataError,
  } = useGetAllSpendingCategoryQuery();

  // Build Order By Options dynamically
  const categoryOptions = useMemo(() => {
    if (!categoryData || !Array.isArray(categoryData)) return [];
    return categoryData.map((cat) => ({
      label: cat.name === "all" ? "All" : cat.name,
      value: cat.name === "all" ? "" : cat.name,
    }));
  }, [categoryData]);

  const handleFilter = (e) => {
    const filterBy = e.target.name;
    if (filterBy === "amount") {
      setValueToDelaySearch({
        property: e.target.name,
        value: e.target.value,
      });
    } else if (filterBy === "createdBy") {
      setValueToDelaySearchCreatedBy({
        property: e.target.name,
        value: e.target.value,
      });
    } else {
      dispatch(
        setFilter({
          property: e.target.name,
          value: e.target.value,
        })
      );
    }
  };

  const handleFilterDropDown = useCallback(
    (e) => {
      const { property, value } = e;
      if (!property) return; // safety check

      dispatch(setFilter({ property, value }));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(setFilter(debounceValueAmount));
  }, [dispatch, debounceValueAmount]);

  useEffect(() => {
    dispatch(setFilter(debounceValueCreatedBy));
  }, [dispatch, debounceValueCreatedBy]);

  return (
    <div className={styles.filter_container}>
      <section className={styles.orderType}>
        <p htmlFor="createdBy">Created by</p>
        <CustomDropdown
          options={createdByOptions}
          placeholder="All"
          fieldName="createdBy"
          handleFilterBy={handleFilterDropDown}
        />
      </section>
      <section className={styles.amount}>
        <label>Amount</label>
        <input
          type="text"
          id="amount"
          name="amount"
          placeholder="9500"
          value={valueToDelaySearch.value}
          onChange={(e) => handleFilter(e)}
        />
      </section>
      <section className={styles.orderType}>
        <p htmlFor="paymentMethod">Payment method</p>
        <CustomDropdown
          options={paymentMethodOptions}
          placeholder="All"
          fieldName="paymentMethod"
          handleFilterBy={handleFilterDropDown}
        />
      </section>
      <section className={styles.orderType}>
        <p htmlFor="spendingCategory">Category</p>
        <CustomDropdown
          options={categoryOptions}
          placeholder="All"
          fieldName="spendingCategory"
          handleFilterBy={handleFilterDropDown}
        />
      </section>
      <section className={styles.orderType}>
        <p htmlFor="expFrom">Expense date from</p>
        <CustomDatePicker
          isFilter={true}
          value={expenseDateStart}
          onChange={(date) => {
            dispatch(
              setFilter({
                property: "expenseDateStart",
                value: date ? date.toISOString().split("T")[0] : "",
              })
            );
          }}
        />
      </section>
      <section className={styles.orderType}>
        <p htmlFor="expTo">Expense date to</p>
        <CustomDatePicker
          isFilter={true}
          value={expenseDateEnd}
          onChange={(date) => {
            dispatch(
              setFilter({
                property: "expenseDateEnd",
                value: date ? date.toISOString().split("T")[0] : "",
              })
            );
          }}
        />
      </section>
    </div>
  );
};
